import React from "react";
import { Link } from "react-router-dom";
import { Palette, Music, Camera, Feather, Users, Globe2, Sparkles, BookOpen, Building2, Flame } from "lucide-react";
import { useContent } from "@/hooks/useContent";
import PageSEO from "@/components/PageSEO";

const IMG_HERO = "https://images.unsplash.com/photo-1783420040194-0bbd06fd6512?crop=entropy&cs=srgb&fm=jpg&q=85";
const IMG_ART = "https://images.unsplash.com/photo-1723463533634-42d479628e7e?crop=entropy&cs=srgb&fm=jpg&q=85";

const disciplines = [
  { icon: Users, t: "Artist Residencies", d: "Seasonal residencies (4–12 weeks) for painters, sculptors, composers, choreographers, and interdisciplinary artists." },
  { icon: Globe2, t: "Cultural Exchange", d: "Bilateral programs with partner sanctuaries, cultural institutions, and indigenous communities across the Americas and beyond." },
  { icon: Music, t: "Music & Dance", d: "Ceremonial performance, seasonal concerts, movement practice, festival programming, and traditional music preservation." },
  { icon: Feather, t: "Storytelling & Literature", d: "Writers-in-residence, oral history projects, poetry, essay, and long-form journalism about regeneration and place." },
  { icon: Camera, t: "Film & Photography", d: "Documentarians, cinematographers, and photojournalists documenting the sanctuary, its people, and its lineages." },
  { icon: Sparkles, t: "Indigenous Knowledge", d: "Partnership with elders and knowledge keepers to preserve, honor, and re-integrate traditional wisdom into contemporary practice." },
  { icon: Palette, t: "Traditional Craft", d: "Weaving, ceramics, woodwork, natural dye, herbalism — hands-on apprenticeships with master craftspeople." },
  { icon: Building2, t: "Architecture & Public Art", d: "Bamboo architecture, functional sculpture, murals, land art, and site-specific installations commissioned across the sanctuary." },
  { icon: Flame, t: "Festivals & Ceremony", d: "Public festivals, ceremonial gatherings, community celebrations, and rites of passage that anchor the cultural calendar." },
  { icon: BookOpen, t: "Creative Research & Publishing", d: "Interdisciplinary research fellowships, documentation projects, and the sanctuary's own publishing imprint — books, journals, catalogues." },
];

export default function Culture() {
  const { t } = useContent("culture");
  return (
    <div>
      <PageSEO
        title={"Culture & Arts | Celestial Seed Sanctuary"}
        description={"Culture & Arts at Celestial Seed Sanctuary. Art is the language through which every department of the institution expresses itself. Programs, residencies, and public gatherings."}
      />
      <section className="relative h-[65vh] overflow-hidden">
        <img src={IMG_HERO} alt="Cultural gathering" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-forest/70 via-forest/50 to-forest/70" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 w-full text-bone">
            <div className="overline text-bone/80 mb-4" data-testid="culture-overline">{t("hero.overline", "Central Department")}</div>
            <h1 className="font-serif text-5xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl" data-testid="culture-title">
              {t("hero.title", "Culture & Arts")}
            </h1>
            <p className="mt-6 text-bone/85 text-lg max-w-2xl" data-testid="culture-lead">
              {t("hero.lead", "Art is not another department. Art is the language through which every department of the institution expresses itself.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#participate" data-testid="culture-hero-cta-participate" className="pill pill-accent">Participate →</a>
              <a href="#disciplines" data-testid="culture-hero-cta-disciplines" className="pill pill-outline text-bone border-bone">Programs & Disciplines</a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick navigation */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-6 border-b border-line">
        <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm" aria-label="Culture section navigation">
          <a href="#purpose" data-testid="culture-nav-purpose" className="text-ink hover:text-terracotta transition-colors">Purpose</a>
          <a href="#disciplines" data-testid="culture-nav-disciplines" className="text-ink hover:text-terracotta transition-colors">Programs & Disciplines</a>
          <a href="#participate" data-testid="culture-nav-participate" className="text-ink hover:text-terracotta transition-colors">Participate</a>
          <Link to="/retreats" data-testid="culture-nav-retreats" className="text-ink hover:text-terracotta transition-colors ml-auto">Retreats →</Link>
          <Link to="/donate" data-testid="culture-nav-fund" className="text-ink hover:text-terracotta transition-colors">Fund the Arts →</Link>
        </nav>
      </section>

      <section id="purpose" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="overline mb-3" data-testid="culture-purpose-overline">{t("purpose.overline", "Purpose")}</div>
          <h2 className="font-serif text-4xl leading-tight" data-testid="culture-purpose-headline">
            {t("purpose.headline", "Culture as a regenerative force — woven into ecology, education, healing, and community.")}
          </h2>
        </div>
        <div className="space-y-4 text-ink-soft leading-relaxed">
          <p data-testid="culture-purpose-body-1">
            {t("purpose.body_1", "Culture & Arts is one of the central regenerative pillars of Celestial Seed. Artistic practice, cultural preservation, interdisciplinary collaboration, and international exchange are treated as core institutional work — not decoration.")}
          </p>
          <p data-testid="culture-purpose-body-2">
            {t("purpose.body_2", "Music at retreats. Architecture as functional sculpture. Natural building as craft. Dance as community practice. Photography documenting regeneration. Film preserving stories. Traditional knowledge honored alongside contemporary practice. These are how the mission becomes visible.")}
          </p>
          <p className="text-xs uppercase tracking-widest text-terracotta pt-2" data-testid="culture-purpose-body-3">
            {t("purpose.body_3", "Fiscally sponsored through Fractured Atlas — eligible for arts, humanities, and cultural preservation funding.")}
          </p>
        </div>
      </section>

      <section id="disciplines" className="max-w-7xl mx-auto px-6 lg:px-10 pb-12">
        <div className="overline mb-6">Programs &amp; Disciplines</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {disciplines.map((d) => (
            <div key={d.t} className="rounded-2xl border border-line bg-surface p-8 hover:shadow-[0_20px_40px_-24px_rgba(44,61,48,0.25)] transition-shadow duration-300">
              <d.icon className="h-5 w-5 text-terracotta" strokeWidth={1.5} />
              <div className="font-serif text-2xl mt-4">{d.t}</div>
              <p className="text-ink-soft text-sm mt-3 leading-relaxed">{d.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="participate" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-12 items-center">
        <img src={IMG_ART} alt="Residency" className="rounded-3xl aspect-[4/5] object-cover" />
        <div>
          <div className="overline mb-3" data-testid="culture-participate-overline">{t("participate.overline", "Participate")}</div>
          <h2 className="font-serif text-4xl leading-[1.05]" data-testid="culture-participate-headline">
            {t("participate.headline", "Residencies, festivals, apprenticeships, exchange.")}
          </h2>
          <ul className="mt-6 space-y-3 text-ink">
            {[
              "Seasonal Artist Residencies (4–12 weeks)",
              "Community &amp; Public Art Commissions",
              "Music, Dance &amp; Ceremony Programming",
              "Documentation &amp; Publishing (film, book, podcast, journal)",
              "Indigenous Knowledge Preservation partnerships",
              "International Cultural Exchange with partner sanctuaries",
              "Creative Apprenticeships within the School of Practical Wisdom",
              "Traditional Craft masterclasses (weaving, ceramics, natural building)",
              "Interdisciplinary Creative Research fellowships",
            ].map((txt) => (
              <li key={txt} className="flex items-start gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" /><span dangerouslySetInnerHTML={{__html: txt}} /></li>
            ))}
          </ul>
          <div className="mt-8 flex gap-3 flex-wrap">
            <Link to="/contact" data-testid="culture-cta-apply" className="pill pill-primary">Propose a Residency</Link>
            <Link to="/volunteer" data-testid="culture-cta-volunteer" className="pill pill-outline">Join the Corps</Link>
            <Link to="/donate" data-testid="culture-cta-fund" className="pill pill-outline">Fund the Arts</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
